import { BrokeragePricing } from '../slices/marketplace/pricing/models/brokerage-pricing.model';
import { MarketplacePricing } from '../slices/marketplace/pricing/models/marketplace-pricing.model';
import { LevelOfService } from '../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { EngagementDocumentType } from '../wallet/models/EngagementDocumentType';

export interface EngagementPaymentPricingDetails {
    pricePerFteDvm: number;
    fteDvmsCount: number;
    creditablePercent: number;
    creditableAmount: number;
    totalPrice: number;
    engagementId: number;
    eventId: string;
    documentType?: EngagementDocumentType;
    documentId?: string;
}

export interface ServicePriceDetails {
    pricePerItem: number;
    itemsCount: number;
    totalPrice: number;
    creditablePercent: number;
    creditableAmount: number;
    firstOccurrencePrice?: number;
    baseFee?: number;
    fteDvms?: number;
}

export class PricesCalculationHelper {
    public static getBrokeragePricingByLevelOfService(
        levelOfService: LevelOfService,
        pricingState: MarketplacePricing
    ): BrokeragePricing {
        switch (levelOfService) {
            case LevelOfService.DiscountBrokerage:
                return pricingState.discountBrokeragePricing;
            case LevelOfService.FullServiceBrokerage:
                return pricingState.fullServiceBrokeragePricing;
            case LevelOfService.StrategicFinancialAdvisoryService:
                return pricingState.sfasPricing;
            default:
                throw new Error(
                    `Unsupported level of service: ${levelOfService}`
                );
        }
    }

    public static calculateSessionPrice(
        selectedNow: number,
        alreadyPurchased: number,
        bundleSize: number,
        pricePerItem: number
    ): number {
        let sessionPrice = 0;

        if (alreadyPurchased > 0) {
            const remainingFreeItems = bundleSize - alreadyPurchased;
            const freeItemsThisSession = Math.max(
                0,
                Math.min(selectedNow, remainingFreeItems)
            );

            const paidItems = selectedNow - freeItemsThisSession;

            sessionPrice = paidItems * pricePerItem;

            return sessionPrice;
        }

        const remainingItems = selectedNow - 1;
        const remainingFreeItems = bundleSize - 1;
        const freeItemsThisSession = Math.max(
            0,
            Math.min(remainingItems, remainingFreeItems)
        );
        const paidItems = remainingItems - freeItemsThisSession;

        sessionPrice = paidItems * pricePerItem + pricePerItem;

        return sessionPrice;
    }

    public static getIdNDAExecutionFeeDetails(
        pricingState: MarketplacePricing,
        fteDvmsCount: number,
        levelOfService: LevelOfService
    ): ServicePriceDetails {
        const brokerage = this.getBrokeragePricingByLevelOfService(levelOfService, pricingState);
        const pricePerItem = brokerage.buyersFees.sellerIdFeePerDvm;
        const totalPrice = fteDvmsCount * pricePerItem;

        const result: ServicePriceDetails = {
            pricePerItem: pricePerItem,
            itemsCount: fteDvmsCount,
            totalPrice: totalPrice,
            creditablePercent: 0,
            creditableAmount: 0,
        };
        return result;
    }

    public static getDataNDAExecutionFeeDetails(
        pricingState: MarketplacePricing,
        fteDvmsCount: number,
        levelOfService: LevelOfService
    ): ServicePriceDetails {
        const brokerage = this.getBrokeragePricingByLevelOfService(levelOfService, pricingState);
        const pricePerItem = brokerage.buyersFees.pipAccessFeePerDvm;
        const totalPrice = fteDvmsCount * pricePerItem;

        const result: ServicePriceDetails = {
            pricePerItem: pricePerItem,
            itemsCount: fteDvmsCount,
            totalPrice: totalPrice,
            creditablePercent: 0,
            creditableAmount: 0,
        };
        return result;
    }

    public static getFinancialModelFeeDetails(
        pricingState: MarketplacePricing,
        fteDvmsCount: number,
        levelOfService: LevelOfService
    ): ServicePriceDetails {
        const brokerage = this.getBrokeragePricingByLevelOfService(levelOfService, pricingState);
        const pricePerItem = brokerage.buyersFees.dfmAccessFeePerDvm;
        const totalPrice = fteDvmsCount * pricePerItem;

        const result: ServicePriceDetails = {
            pricePerItem: pricePerItem,
            itemsCount: fteDvmsCount,
            totalPrice: totalPrice,
            creditablePercent: 0,
            creditableAmount: 0,
        };
        return result;
    }

    public static getLateFeePrice(
        pricePerDay: number,
        extansionDays: number,
        fteDvms: number,
        buyersAmount: number,
        firstOccurrencePrice: number,
        isFirstOccurrence: boolean
    ): ServicePriceDetails {
        let totalPrice = 0;
        let remainingExtensionDays = extansionDays;
        if (isFirstOccurrence) {
            totalPrice = firstOccurrencePrice * fteDvms;
            remainingExtensionDays--;
        }

        totalPrice +=
            pricePerDay * remainingExtensionDays * fteDvms;
        const result: ServicePriceDetails = {
            pricePerItem: pricePerDay,
            itemsCount: extansionDays,
            totalPrice: totalPrice,
            creditablePercent: 0,
            creditableAmount: 0,
            firstOccurrencePrice: firstOccurrencePrice,
        };

        return result;
    }

    public static getSuspensionFeePrice(
        pricing: BrokeragePricing,
        fteDvmsCount: number,
        monthesCount: number
    ): ServicePriceDetails {
        const total =
            pricing.transactionFees.suspensionFee +
            pricing.transactionFees.suspensionFeePerDvm * fteDvmsCount;

        const result: ServicePriceDetails = {
            pricePerItem: pricing.transactionFees.suspensionFeePerDvm,
            itemsCount: monthesCount,
            baseFee: pricing.transactionFees.suspensionFee,
            totalPrice: total,
            creditablePercent: 0,
            creditableAmount: 0,
        };

        return result;
    }

    public static getChengeTempoFeePrice(
        pricing: BrokeragePricing,
        fteDvmsCount: number
    ): ServicePriceDetails {
        const total =
            pricing.transactionFees.tempoChangeFeePerDvm * fteDvmsCount;

        const result: ServicePriceDetails = {
            pricePerItem: pricing.transactionFees.tempoChangeFeePerDvm,
            itemsCount: 0,
            totalPrice: total,
            creditablePercent: 0,
            creditableAmount: 0,
            fteDvms: fteDvmsCount,
        };

        return result;
    }
}
